<!--
/**
 * FormKitCountrySelect
 *
 * Custom FormKit input that wraps PrimeVue Select (single) or MultiSelect (multi) for
 * country selection. Defaults to the static country list from member-webapp-shared
 * country-options. Callers that omit `options` still get countries only.
 *
 * Pass `options` to replace that list. A flat `{ value, label }[]` stays one list.
 * Groups shaped `{ label, items: { value, label }[] }[]` render as option groups.
 * Charity operating areas pass `hifoOperatingAreaGroups` for this.
 *
 * Supports dual mode: inside a FormKit form (context) or standalone with v-model.
 *
 * @description Country select input with optional multi-select (taglist) and filtering.
 *
 * FormKit usage (countries only):
 *   <FormKit type="countrySelect" name="country" :filter="true" :showClear="true" />
 *
 * FormKit usage with a custom list (charity operating areas):
 *   <FormKit type="countrySelect" name="operatingAreas" label="Charity operating areas"
 *            :taglist="true" :filter="true" :showClear="true"
 *            :options="hifoOperatingAreaGroups" />
 *
 * Standalone usage (e.g. in a card/record view):
 *   <FormKitCountrySelect v-model="selectedCountry" :taglist="false" :filter="true"
 *                         placeholder="Choose country..." />
 *
 * @prop {Object} [context] - FormKit framework context (provided when used as FormKit input).
 * @prop {string|string[]|null} [modelValue] - Selected value: string for single, string[] for taglist.
 * @prop {boolean} [taglist=false] - If true, use MultiSelect (multiple values); otherwise Select (single).
 * @prop {string} [placeholder=''] - Placeholder text when nothing selected. Empty = non-breaking space (no visible placeholder).
 * @prop {boolean} [filter=false] - Enable search/filter in the list.
 * @prop {boolean} [showClear=false] - Show clear button when a value is selected.
 * @prop {boolean} [disabled=false] - Disable the input.
 * @prop {Array} [options=countryOptions] - Flat `{ value, label }[]`, or groups `{ label, items }[]`.
 * @emits {string|string[]|null} update:modelValue - Emitted when selection changes (single value or array when taglist).
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="wrapperClass">
    <MultiSelect
      v-if="taglist"
      :id="inputId"
      :model-value="localValue"
      :options="selectableCountryOptions"
      option-label="label"
      option-value="value"
      v-bind="optionGroupBindings"
      :placeholder="displayPlaceholder"
      :filter="filter"
      :filter-placeholder="filterPlaceholder"
      display="chip"
      :disabled="disabled"
      aria-label="Country selection"
      class="w-full"
      data-testid="formkit-country-select-input"
      :show-clear="showClear"
      :max-selected-labels="3"
      @update:model-value="onInput"
    />
    <Select
      v-else
      :id="inputId"
      :model-value="localValue"
      :options="selectableCountryOptions"
      option-label="label"
      option-value="value"
      v-bind="optionGroupBindings"
      :placeholder="displayPlaceholder"
      :filter="filter"
      :filter-placeholder="filterPlaceholder"
      :disabled="disabled"
      aria-label="Country selection"
      class="w-full"
      data-testid="formkit-country-select-input"
      :show-clear="showClear"
      @update:model-value="onInput"
    />
  </div>
</template>

<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import Select from 'primevue/select'
import type { PropType } from 'vue'
import { computed } from 'vue'
import {
  countryOptions,
  DIVIDER,
} from '~/../member-webapp-shared/services/country-options'
import { useFormKitSelect } from '~/composables/useFormKitSelect'

type SelectOption = { value: string; label: string; disabled?: boolean }
type SelectOptionGroup = { label: string; items: SelectOption[] }

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        placeholder?: string
        filter?: boolean
        disabled?: boolean
        showClear?: boolean
        taglist?: boolean
        options?: SelectOption[] | SelectOptionGroup[]
      }
    >,
    default: undefined,
  },
  modelValue: {
    type: [Array, String] as PropType<string[] | string | null>,
    default: undefined,
  },
  taglist: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  filter: { type: Boolean, default: false },
  showClear: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  options: {
    type: Array as PropType<SelectOption[] | SelectOptionGroup[]>,
    default: countryOptions,
  },
})

const emit = defineEmits<{
  'update:modelValue': [value: string[] | string | null]
}>()

const sourceOptions = computed(() => props.context?.options ?? props.options)

const isGrouped = (
  options: SelectOption[] | SelectOptionGroup[]
): options is SelectOptionGroup[] => {
  const firstOption = options[0]
  return firstOption !== undefined && 'items' in firstOption
}

const grouped = computed(() => isGrouped(sourceOptions.value))

const selectableOptions = (options: SelectOption[]) =>
  options.filter((opt) => opt.value !== '' && opt.value !== DIVIDER)

const selectableCountryOptions = computed(() => {
  if (isGrouped(sourceOptions.value)) {
    return sourceOptions.value
      .map((group) => ({
        label: group.label,
        items: selectableOptions(group.items),
      }))
      .filter((group) => group.items.length > 0)
  }

  return selectableOptions(sourceOptions.value)
})

const optionGroupBindings = computed(() =>
  grouped.value
    ? { optionGroupLabel: 'label', optionGroupChildren: 'items' }
    : {}
)

const filterPlaceholder = computed(() => {
  const options = sourceOptions.value
  if (!isGrouped(options)) return 'Search countries'

  const labels = options
    .filter((group) => selectableOptions(group.items).length > 0)
    .map((group) => group.label)
  if (labels.length === 0) return 'Search countries'
  if (labels.length < 4) return `Search ${labels.join(', ')}`

  return `Search ${labels.slice(0, 4).join(', ')}...`
})

const {
  attrs,
  inputId,
  disabled,
  taglist,
  filter,
  showClear,
  isRequired,
  displayPlaceholder,
  localValue,
  onInput,
  wrapperClass,
} = useFormKitSelect({ props, emit })
</script>
